<template>
  <div class="pa-3">
    <!-- TOP BAR: Search + Add Tenant -->
    <v-row class="mb-4" align="center">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search by Display Name or Reference"
          outlined dense clearable
          class="rounded-lg small-text-field"
          hide-details
        />
      </v-col>
      <v-col cols="12" md="6" class="text-right">
        <v-btn
          @click="$router.push('/Addtenant')"
          small class="save-btn px-6"
          style="border-radius: 20px; font-weight: bold; font-size: 0.7rem;"
        >
          <v-icon left small>mdi-plus</v-icon> Add Tenant
        </v-btn>
      </v-col>
    </v-row>

    <!-- STATUS TABS - Compact -->
    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      color="primary"
      grow
      class="mb-3 compact-tabs"
    >
      <v-tab>
        <v-badge
          :content="tenants.length"
          color="primary"
          overlap
          offset-x="8"
          offset-y="8"
        >
          <span class="caption font-weight-medium">All</span>
        </v-badge>
      </v-tab>
      <v-tab>
        <v-badge
          :content="activeCount"
          color="green"
          overlap
          offset-x="8"
          offset-y="8"
        >
          <span class="caption font-weight-medium">
            <v-icon left x-small>mdi-check-circle</v-icon> Active
          </span>
        </v-badge>
      </v-tab>
      <v-tab>
        <v-badge
          :content="expiredCount"
          color="orange"
          overlap
          offset-x="8"
          offset-y="8"
        >
          <span class="caption font-weight-medium">
            <v-icon left x-small>mdi-clock-alert</v-icon> Expired
          </span>
        </v-badge>
      </v-tab>
      <v-tab>
        <v-badge
          :content="inactiveCount"
          color="red"
          overlap
          offset-x="8"
          offset-y="8"
        >
          <span class="caption font-weight-medium">
            <v-icon left x-small>mdi-cancel</v-icon> Inactive
          </span>
        </v-badge>
      </v-tab>
    </v-tabs>

    <!-- FULL WIDTH TENANTS TABLE -->
    <v-data-table
      :headers="headers"
      :items="filteredTenants"
      :search="search"
      :items-per-page="15"
      class="elevation-2 compact-table"
      style="border-radius: 12px; overflow: hidden;"
      :expanded.sync="expanded"
      show-expand
      dense
    >
      <!-- Status Chip -->
      <template v-slot:item.status="{ item }">
        <v-chip :color="item.statusColor" dark x-small>
          {{ item.statusLabel }}
        </v-chip>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex align-center justify-center" style="gap: 2px;">
          <v-btn icon x-small @click="editTenant(item)" class="action-btn">
            <v-icon x-small color="grey darken-2">mdi-pencil</v-icon>
          </v-btn>
          <v-btn icon x-small @click="confirmDelete(item)" class="action-btn">
            <v-icon x-small color="red">mdi-delete</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- EXPANDED ROW -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length" class="pa-3" style="background: #f8f9fa;">
          <v-row dense>
            <v-col cols="12" md="4" class="py-1"><strong class="caption black--text">Reference:</strong> <span class="caption black--text">{{ item.tenantId }}</span></v-col>
            <v-col cols="12" md="4" class="py-1"><strong class="caption black--text">Display Name:</strong> <span class="caption black--text">{{ item.displayName }}</span></v-col>
            <v-col cols="12" md="4" class="py-1">
              <strong class="caption black--text">Status:</strong> 
              <v-chip :color="item.statusColor" dark x-small>{{ item.statusLabel }}</v-chip>
            </v-col>
          </v-row>
          <v-row dense v-if="item.activeLeaseInfo" class="mt-1">
            <v-col cols="12">
              <v-chip small color="info" text-color="white" class="caption">
                <v-icon left x-small>mdi-home</v-icon>
                {{ item.activeLeaseInfo.leaseStatus }}: {{ item.activeLeaseInfo.propertyName }} ({{ item.activeLeaseInfo.leaseReference }})
              </v-chip>
            </v-col>
          </v-row>
          <v-row dense class="mt-2">
            <v-col cols="12" md="4">
              <v-btn block class="golden-btn" small @click="goToAttachments(item)">
                <v-icon left x-small>mdi-folder-multiple</v-icon> Attachments
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn block class="golden-btn" small @click="goToNotes(item)">
                <v-icon left x-small>mdi-note-text</v-icon> Notes
              </v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn block class="golden-btn" small @click="goToReports(item)">
                <v-icon left x-small>mdi-chart-bar</v-icon> Reports
              </v-btn>
            </v-col>
          </v-row>
        </td>
      </template>
    </v-data-table>

    <!-- DELETE CONFIRM DIALOG -->
    <v-dialog v-model="deleteDialog" max-width="450">
      <v-card class="pa-3">
        <v-card-title class="red--text text-subtitle-1 font-weight-bold">Delete Tenant?</v-card-title>
        <v-card-text class="caption black--text">
          Are you sure you want to delete tenant:<br>
          <strong>{{ tenantToDelete?.displayName }} ({{ tenantToDelete?.tenantId }})</strong>?<br><br>
          This action <strong>cannot be undone</strong>.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="red" dark small @click="deleteTenant">Delete Tenant</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getFirestore, collection, getDocs, deleteDoc, doc, addDoc } from "firebase/firestore";
import { firebaseApp } from "../firebaseConfig";

const db = getFirestore(firebaseApp);

// LOG EVENT FUNCTION
async function logEvent(action, target = "", type = "Tenant Management") {
  const currentUser = JSON.parse(localStorage.getItem("currentUser") || "{}");
  const event = {
    date: Math.floor(Date.now() / 1000),
    eventType: type,
    eventAction: action,
    user: currentUser.mobile || currentUser.email || "System",
    userName: `${currentUser.name || ""} ${currentUser.lastname || ""}`.trim(),
    targetEmail: target,
  };
  try {
    await addDoc(collection(db, "events"), event);
  } catch (e) {
    console.error("Failed to log event:", e);
  }
}

export default {
  data() {
    return {
      search: "",
      activeTab: 0,
      expanded: [],
      deleteDialog: false,
      tenantToDelete: null,
      tenants: [],
      headers: [
        { text: "Reference", value: "tenantId", width: "120px" },
        { text: "Display Name", value: "displayName" },
        { text: "Status", value: "status", align: "center", width: "100px" },
        { text: "", value: "data-table-expand", width: "40px" },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "100px" },
      ],
    };
  },

  async mounted() {
    await this.fetchTenants();
  },

  computed: {
    // Count tenants by status
    activeCount() {
      return this.tenants.filter(t => t.statusType === 'active').length;
    },
    expiredCount() {
      return this.tenants.filter(t => t.statusType === 'expired').length;
    },
    inactiveCount() {
      return this.tenants.filter(t => t.statusType === 'inactive').length;
    },

    // Filter tenants based on active tab and sort consistently
    filteredTenants() {
      let filtered = [...this.tenants];
      
      // Filter by status based on active tab
      if (this.activeTab === 1) {
        filtered = filtered.filter(t => t.statusType === 'active');
      } else if (this.activeTab === 2) {
        filtered = filtered.filter(t => t.statusType === 'expired');
      } else if (this.activeTab === 3) {
        filtered = filtered.filter(t => t.statusType === 'inactive');
      }
      // Tab 0 (All) - no filtering
      
      // Sort: Active first, then others (Expired and Inactive) sorted by tenantId
      return filtered.sort((a, b) => {
        // If this is the "All" tab (tab 0), prioritize Active first
        if (this.activeTab === 0) {
          // Active tenants come first
          if (a.statusType === 'active' && b.statusType !== 'active') return -1;
          if (a.statusType !== 'active' && b.statusType === 'active') return 1;
        } else {
          // For specific tabs, sort by status order
          const statusOrder = { active: 0, expired: 1, inactive: 2 };
          const orderA = statusOrder[a.statusType] || 2;
          const orderB = statusOrder[b.statusType] || 2;
          
          if (orderA !== orderB) return orderA - orderB;
        }
        
        // If both have same status or both are not active (on All tab),
        // sort by tenantId
        return a.tenantId.localeCompare(b.tenantId);
      });
    }
  },

  methods: {
    /**
     * Check if a lease is active and not expired
     * @param {Object} lease - The lease document data
     * @returns {Object} - Status object with isActive, isExpired, and details
     */
    checkLeaseStatus(lease) {
      // Check if lease has an end date
      const endDate = lease.endDate || lease.expiryDate || lease.leaseEnd || lease.end || lease.expiresAt;
      
      if (!endDate) {
        // If no end date, just check status
        return {
          isActive: lease.status === "active",
          isExpired: false,
          hasEndDate: false
        };
      }

      // Convert to Date object if it's a Timestamp or string
      let endDateTime;
      if (endDate.toDate && typeof endDate.toDate === 'function') {
        // Firestore Timestamp
        endDateTime = endDate.toDate();
      } else if (endDate instanceof Date) {
        endDateTime = endDate;
      } else {
        endDateTime = new Date(endDate);
      }

      const now = new Date();
      const isExpired = endDateTime <= now;
      const isActive = lease.status === "active" && !isExpired;

      return {
        isActive: isActive,
        isExpired: isExpired,
        hasEndDate: true,
        endDate: endDateTime
      };
    },

    async fetchTenants() {
      try {
        // Fetch all tenants
        const tenantsSnap = await getDocs(collection(db, "tenants"));
        const tenants = tenantsSnap.docs.map(doc => {
          const d = doc.data();
          return {
            id: doc.id,
            tenantId: d.reference || "N/A",
            displayName: d.displayAs || "No Name",
            email: d.email || "",
          };
        });

        // Fetch ALL leases
        const leasesSnap = await getDocs(collection(db, "Leases"));
        
        // Group leases by tenantId
        const leasesByTenant = {};
        leasesSnap.docs.forEach(doc => {
          const leaseData = doc.data();
          const tenantId = leaseData.tenantId;
          
          if (!tenantId) return;
          
          if (!leasesByTenant[tenantId]) {
            leasesByTenant[tenantId] = [];
          }
          
          leasesByTenant[tenantId].push({
            id: doc.id,
            ...leaseData
          });
        });

        // Map tenants with status information
        this.tenants = tenants.map(tenant => {
          const tenantLeases = leasesByTenant[tenant.tenantId] || [];
          
          // Check each lease for status
          let statusType = 'inactive'; // default
          let statusLabel = 'Inactive';
          let statusColor = 'red';
          let activeLeaseInfo = null;

          // Find the best lease status
          for (const lease of tenantLeases) {
            const leaseStatus = this.checkLeaseStatus(lease);
            
            if (leaseStatus.isActive) {
              // Found an active lease - this is the best status
              statusType = 'active';
              statusLabel = 'Active';
              statusColor = 'green';
              activeLeaseInfo = {
                propertyName: lease.propertyName || 'N/A',
                leaseReference: lease.leaseReference || 'N/A',
                leaseStatus: 'Active Lease'
              };
              break; // Active is the best status, stop checking
            } else if (lease.status === "active" && leaseStatus.isExpired) {
              // Found an expired lease (status is active but expired)
              if (statusType !== 'active') {
                statusType = 'expired';
                statusLabel = 'Expired';
                statusColor = 'orange';
                activeLeaseInfo = {
                  propertyName: lease.propertyName || 'N/A',
                  leaseReference: lease.leaseReference || 'N/A',
                  leaseStatus: 'Expired Lease'
                };
              }
            }
          }

          return {
            ...tenant,
            statusType,
            statusLabel,
            statusColor,
            activeLeaseInfo
          };
        });
        
      } catch (e) {
        console.error("Error loading tenants:", e);
      }
    },

    editTenant(item) {
      this.$router.push(`/EditTenant/${item.id}`);
    },

    goToAttachments(item) {
      this.$router.push({ name: "Attachments", params: { collection: "tenants", id: item.id } });
    },
    goToNotes(item) {
      this.$router.push({ name: "Notes", params: { id: item.id }, query: { collection: "tenants" } });
    },
    goToReports(item) {
      this.$router.push({ name: "Reports", params: { id: item.id }, query: { collection: "tenants" } });
    },

    confirmDelete(item) {
      this.tenantToDelete = item;
      this.deleteDialog = true;
    },

    async deleteTenant() {
      if (!this.tenantToDelete) return;

      try {
        await deleteDoc(doc(db, "tenants", this.tenantToDelete.id));
        this.tenants = this.tenants.filter(t => t.id !== this.tenantToDelete.id);

        // LOG TO ACTIVITIES
        await logEvent(
          "Tenant Deleted",
          this.tenantToDelete.displayName,
          "Tenant Management"
        );

        alert("Tenant deleted successfully!");
      } catch (e) {
        console.error("Delete failed:", e);
        alert("Failed to delete tenant");
      } finally {
        this.deleteDialog = false;
        this.tenantToDelete = null;
      }
    },
  },
};
</script>

<style scoped>
/* FULL WIDTH */
:deep(.v-data-table > .v-data-table__wrapper) {
  overflow-x: auto;
}

/* Compact Table */
.compact-table :deep(.v-data-table-header th) {
  font-size: 0.8rem !important;      /* was 0.6rem */
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 8px 10px !important;      /* was 6px */
  background-color: #f0f0f0 !important;
  color: #000000 !important;
  border-bottom: 2px solid #ccc !important;
}

.compact-table :deep(.v-data-table-header th span) {
  color: #000000 !important;
}

.compact-table :deep(tbody td) {
  font-size: 0.875rem !important;    /* was 0.7rem */
  padding: 6px 10px !important;      /* was 4px */
  color: #000000 !important;
}

.compact-table :deep(.v-data-table__expanded__content) {
  font-size: 0.85rem !important;     /* was 0.65rem */
}

/* Small text field */
.small-text-field :deep(.v-input__slot) {
  min-height: 40px !important;       /* was 36px */
}

.small-text-field :deep(.v-label) {
  font-size: 0.875rem !important;    /* was 0.7rem */
  color: #000000 !important;
}

.small-text-field :deep(.v-input__control input) {
  font-size: 0.875rem !important;    /* was 0.7rem */
  color: #000000 !important;
}

/* Black text */
.black--text {
  color: #000000 !important;
}

/* Compact Tabs */
.compact-tabs :deep(.v-tab) {
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;    /* was 0.7rem */
  min-height: 40px !important;       /* was 36px */
  padding: 0 14px !important;
  letter-spacing: 0.3px;
}

/* Centered badge content */
.compact-tabs :deep(.v-tab .v-badge__badge) {
  font-size: 0.7rem !important;      /* was 0.55rem */
  height: 20px !important;
  min-width: 20px !important;
  padding: 0 5px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  line-height: 1 !important;
  text-align: center !important;
}

.compact-tabs :deep(.v-tabs-bar) {
  border-bottom: 2px solid #e0e0e0;
}

.compact-tabs :deep(.v-tab--active) {
  color: #1976d2 !important;
}

/* GOLDEN BUTTONS */
.golden-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  border-radius: 10px !important;
  font-weight: bold !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  text-transform: none !important;
  font-size: 0.8rem !important;      /* was 0.65rem */
  min-height: 34px !important;       /* was 30px */
}

.golden-btn .v-icon {
  color: #000000 !important;
}

.save-btn {
  background: linear-gradient(0deg, #f1c284 0%, #e7b87a 100%) !important;
  color: #000000 !important;
  box-shadow: 0 2px 8px rgba(241, 194, 132, 0.3) !important;
  font-size: 0.875rem !important;    /* was 0.7rem */
  min-height: 36px !important;
}

.save-btn .v-icon {
  color: #000000 !important;
}

/* Action buttons */
.action-btn {
  width: 28px !important;            /* was 24px */
  height: 28px !important;
  min-width: 28px !important;
  min-height: 28px !important;
}

.action-btn .v-icon {
  font-size: 18px !important;        /* was 16px */
}

/* Chip sizes */
:deep(.v-chip) {
  font-size: 0.75rem !important;     /* was 0.55rem */
  height: 22px !important;
}

/* Dialog text */
.v-dialog .v-card-title {
  font-size: 1rem !important;        /* was 0.9rem */
  color: #000000 !important;
}

.v-dialog .v-card-text {
  font-size: 0.875rem !important;    /* was 0.75rem */
  color: #000000 !important;
}

.v-dialog .v-btn {
  font-size: 0.8rem !important;      /* was 0.7rem */
}

/* Responsive adjustments — make it BIGGER on mobile, not smaller */
@media (max-width: 768px) {
  .compact-table :deep(.v-data-table-header th) {
    font-size: 0.75rem !important;   /* was 0.5rem */
    padding: 6px 8px !important;
  }

  .compact-table :deep(tbody td) {
    font-size: 0.8rem !important;    /* was 0.6rem */
    padding: 5px 8px !important;
  }

  .compact-tabs :deep(.v-tab) {
    font-size: 0.75rem !important;   /* was 0.6rem */
    padding: 0 10px !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>